import { apiClient } from './client';

/**
 * Wire shape for ROADMAP #43.2 phase 1B:
 *   - `GET /api/v1/sessions/me/motion`               (participant cross-tenant)
 *   - `GET /api/v1/tenants/{id}/motion`              (tenant_admin Dashboard)
 *   - `GET /api/v1/tenants/{id}/sessions/{sid}/motion` (tenant_admin detail)
 *
 * Mirrors api/motion.ts on the backend; same shape as the cognitive
 * client so the Dashboard wiring stays symmetric.
 */

export interface MotionScore {
  id: string;
  session_id: string;
  phase: string;
  bimanual_dexterity: number | null;
  idle_time_fraction: number | null;
  left_mean_speed: number | null;
  right_mean_speed: number | null;
  left_max_speed: number | null;
  right_max_speed: number | null;
  left_gyro_mean: number | null;
  right_gyro_mean: number | null;
  left_gyro_max: number | null;
  right_gyro_max: number | null;
  /** Economy of motion — distance each hand travelled over the phase
   *  (∫|v| dt, cm). Relative signal, not a certified absolute distance
   *  (consumer-IMU drift). Null on rows scored before motion-v1.1.0. */
  left_path_length_cm?: number | null;
  right_path_length_cm?: number | null;
  total_path_length_cm?: number | null;
  /** Drift-robust v2 metrics (SENSOR-DRIFT-ROBUST-METRICS) — never
   *  integrate to position. Null on rows scored before motion-v2.0.0.
   *   - angular path length ∫|ω| dt (deg): drift-robust economy of motion
   *   - smoothness_ldlj: log dimensionless jerk (≤0, higher = smoother)
   *   - tremor_power: 8–12 Hz band power of raw |a| (lower = steadier)
   *   - bidex_v2: gyro-envelope bimanual coupling ([-1,1], higher = coupled)
   *   - active_duration_fraction: gyro-based complement of idle ([0,1]) */
  left_angular_path_deg?: number | null;
  right_angular_path_deg?: number | null;
  total_angular_path_deg?: number | null;
  left_smoothness_ldlj?: number | null;
  right_smoothness_ldlj?: number | null;
  left_tremor_power?: number | null;
  right_tremor_power?: number | null;
  bidex_v2?: number | null;
  active_duration_fraction?: number | null;
  computed_by_version: string;
  scored_at: string;
  started_at: string | null;
  procedure_name: string | null;
  /** ROADMAP #44 — matches public.master_baselines.procedure_group. */
  procedure_group?: string | null;
  participant_user_id: string | null;
  participant_label: string | null;
  /** Originating tenant (ROADMAP #54) — drives the participant
   *  Dashboard's this-tenant/all-tenants filter. */
  tenant_id?: string | null;
  tenant_slug: string | null;
  tenant_name: string | null;
}

export interface MotionAverages {
  bimanual_dexterity: number | null;
  idle_time_fraction: number | null;
  left_mean_speed: number | null;
  right_mean_speed: number | null;
  left_max_speed: number | null;
  right_max_speed: number | null;
  left_gyro_mean: number | null;
  right_gyro_mean: number | null;
  left_gyro_max: number | null;
  right_gyro_max: number | null;
  /** Drift-robust aggregates (SENSOR-DRIFT-ROBUST-METRICS) — angular path
   *  is the dashboard's new economy-of-motion headline. */
  total_angular_path_deg?: number | null;
  bidex_v2?: number | null;
  active_duration_fraction?: number | null;
  session_count: number;
}

export interface MotionParticipantBreakdown {
  participant_user_id: string;
  participant_label: string | null;
  averages: MotionAverages;
}

import type { FacultyReference, MasteryRate } from './cognitive';

export interface MotionTenantSummary {
  overall: MotionAverages;
  by_participant: MotionParticipantBreakdown[];
  procedures: string[];
  filter_procedure: string | null;
  faculty_bimanual?: FacultyReference | null;
  faculty_duration?: FacultyReference | null;
  mastery_rates?: MasteryRate[];
}

export async function listMyMotionScores(
  limit = 200,
  procedure?: string | null,
): Promise<MotionScore[]> {
  const params: Record<string, string | number> = { limit };
  if (procedure) params.procedure = procedure;
  const { data } = await apiClient.get<MotionScore[]>(
    '/api/v1/sessions/me/motion',
    { params },
  );
  return data;
}

export async function getTenantMotionSummary(
  tenantId: string,
  procedure?: string | null,
): Promise<MotionTenantSummary> {
  const params: Record<string, string> = {};
  if (procedure) params.procedure = procedure;
  const { data } = await apiClient.get<MotionTenantSummary>(
    `/api/v1/tenants/${tenantId}/motion`,
    { params },
  );
  return data;
}

export async function getSessionMotionDetail(
  tenantId: string,
  sessionId: string,
): Promise<MotionScore[]> {
  const { data } = await apiClient.get<MotionScore[]>(
    `/api/v1/tenants/${tenantId}/sessions/${sessionId}/motion`,
  );
  return data;
}
